import { Button } from "@components/button";
import { Modal } from "@components/modal";
import { RowTable } from "@components/row-table";
import { Config, DoubleText } from "@components/table";
import classNames from "classnames";
import styles from "./payoutModal.module.scss";
import { useAuth } from "@features/auth";
import { useForm } from "react-hook-form";
import { RadioButton } from "@components/radio-button";
import { AdminPayOutOrder } from "@features/admin/payOut/api";
import { EllipsisText } from "@components/ellipsis-text";
import { formatRubles, formatUsdt } from "@helpers/format";
import { formatDate, formatTime } from "@helpers/date";
import React from "react";
import { useAdminPayOutOrders } from "@features/admin/payOut";
import { FlexLoader } from "@components/loader";
import { HistoryModal } from "./historyModal";
import { useBoolState } from "@hooks/use-bool-state";

const config: Config<AdminPayOutOrder> = [
  {
    label: "ID заявки",
    source: "id",
    renderComponent: (data) => (
      <EllipsisText text={data} startLength={10} copy />
    ),
  },
  {
    label: "invid",
    source: "invid",
    renderComponent: (data) => <EllipsisText text={data} copy />,
  },
  {
    label: "Email Трейдер",
    source: "trader_email",
    renderComponent: (data) => (
      <EllipsisText text={data} startLength={10} copy />
    ),
  },
  {
    label: "Email Мерч",
    source: "merchant_email",
    renderComponent: (data) => (
      <EllipsisText text={data} startLength={10} copy />
    ),
  },
  {
    label: "Доход трейдер",
    source: "trader_ern_usdt",
    renderComponent: (data, item) =>
      item.trader_ern_rub != null ? (
        <DoubleText
          text={formatUsdt(data)}
          subText={formatRubles(item.trader_ern_rub)}
        />
      ) : (
        formatUsdt(data)
      ),
  },
  {
    label: "Сумма",
    source: "sum",
    renderComponent: (data, item) => {
      return (
        <DoubleText
          text={formatUsdt(item.sum_usdt)}
          subText={formatRubles(data)}
        />
      );
    },
  },
  { label: "Курс", source: "exchange_rate" },
  { label: "Статус", source: "status" },
  {
    label: "Списание с мерча",
    source: "merchant_spend_usdt",
    renderComponent: (data, item) => (
      <DoubleText
        text={formatUsdt(data)}
        subText={formatRubles(item.merchant_spend_rub)}
      />
    ),
  },
  { label: "Реквизит", source: "requisite" },
  { label: "Метод", source: "method" },
  {
    label: "Дата назначения на трейдера",
    source: "assigned_to_trader_time_unix",
    renderComponent: (data) =>
      data && (
        <DoubleText
          text={formatTime(new Date(data * 1000))}
          subText={formatDate(new Date(data * 1000))}
        />
      ),
  },
  {
    label: "Дата и время закрытия",
    source: "closed_time_unix",
    renderComponent: (data) =>
      data && (
        <DoubleText
          text={formatTime(new Date(data * 1000))}
          subText={formatDate(new Date(data * 1000))}
        />
      ),
  },
  {
    label: "Дата создания заявки",
    source: "created_at_unix",
    renderComponent: (data) => (
      <DoubleText
        text={formatTime(new Date(data * 1000))}
        subText={formatDate(new Date(data * 1000))}
      />
    ),
  },
];

type Props = {
  isModalOpen: boolean;
  hideModal: () => void;
  data: AdminPayOutOrder;
};

type FormType = {
  type: "complete" | "cancel" | "toPool";
};

export const AdminPayoutModal = ({ hideModal, isModalOpen, data }: Props) => {
  const { isAdmin } = useAuth();
  const { control, handleSubmit, reset } = useForm<FormType>();
  const {
    state,
    completePayOutOrder,
    cancelPayOutOrderBySupport,
    getAdminPayOutOrders,
  } = useAdminPayOutOrders();
  const [isHistoryModalOpen, showHistoryModal, hideHistoryModal] =
    useBoolState(false);

  const onClose = () => {
    reset();
    hideModal();
  };

  const submit = (form: FormType) => {
    let promise: Promise<unknown> | null = null;
    switch (form.type) {
      case "cancel":
        promise = cancelPayOutOrderBySupport(data.id);
        break;
      case "toPool":
        promise = cancelPayOutOrderBySupport(data.id, true);
        break;
      case "complete":
        promise = completePayOutOrder(data.id);
        break;
    }
    if (promise != null) {
      promise.then(() => {
        onClose();
        getAdminPayOutOrders();
      });
    }
  };

  const openHistory = () => {
    showHistoryModal();
  };

  return (
    <>
      <Modal
        isOpen={isModalOpen}
        onClose={onClose}
        title={" "}
        className={styles.modal}
      >
        {state.modalLoading ? (
          <FlexLoader style={{ width: "200px", height: "200px" }} />
        ) : (
          <form onSubmit={handleSubmit(submit)}>
            <div className={styles.wrapper}>
              <RowTable config={config} data={data} className={styles.table} />
              <div className={styles.options}>
                <div className={classNames(styles.block, styles.info)}>
                  Файлы трейдера
                  <div className={styles.linksWrapper}>
                    {data.trader_files?.map((check, index) => (
                      <React.Fragment key={index}>
                        <a
                          href={check}
                          target="_blank"
                          className="underline"
                          style={{ textWrap: "nowrap" }}
                        >
                          {check}
                        </a>
                        <br />
                      </React.Fragment>
                    ))}
                  </div>
                </div>
                <div className={classNames(styles.block, styles.info)}>
                  Файлы мерча
                  <div className={styles.linksWrapper}>
                    {data.merchant_files?.map((check, index) => (
                      <React.Fragment key={index}>
                        <a
                          href={check}
                          target="_blank"
                          className="underline"
                          style={{ textWrap: "nowrap" }}
                        >
                          {check}
                        </a>
                        <br />
                      </React.Fragment>
                    ))}
                  </div>
                </div>

                <RadioButton
                  name="type"
                  control={control}
                  className={styles.input}
                  options={[
                    { label: "Подтверждение вывода", value: "complete" },
                    { label: "Вернуть в пул", value: "toPool" },
                    { label: "Отмена заявки", value: "cancel" },
                    // { label: "Изменить статус на ожидание", value: "pending" },
                  ]}
                  disabled={!isAdmin}
                />
              </div>
            </div>
            <div style={{ display: "flex", gap: "10px" }}>
              {data.trader_history && data.trader_history.length > 0 && (
                <Button buttonType="button" onClick={openHistory}>
                  Посмотреть историю
                </Button>
              )}
              <Button disabled={!isAdmin}>Сохранить</Button>
            </div>
          </form>
        )}
      </Modal>
      <HistoryModal
        isModalOpen={isHistoryModalOpen}
        hideModal={hideHistoryModal}
        data={data}
      ></HistoryModal>
    </>
  );
};
